import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { useLiveRef } from '@gitbook/util-react-hooks';

import { useIsCurrentScreen } from './useIsCurrentScreen';

/**
 * Track a page view on the current screen.
 */
export function useSetAnalyticsScreen(
    screen:
        | analytics.AnalyticsScreen
        | (analytics.AnalyticsScreenProperties & analytics.AnalyticsEventContext)
        | null
        | void,
    deps: any[] = []
) {
    const previousScreenRef = React.useRef<analytics.AnalyticsScreen | null>(null);
    const isCurrentScreen = useIsCurrentScreen();
    const hasScreen = Boolean(screen);
    const screenRef = useLiveRef(screen);

    // this previous screen needs to be updated just before we show the screen
    // eg. go from a falsy screen to a truthy screen
    React.useEffect(() => {
        if (hasScreen) {
            previousScreenRef.current = analytics.getCurrentScreen();
        }
    }, [hasScreen]);

    React.useEffect(() => {
        const screen = screenRef.current;
        if (!hasScreen || !isCurrentScreen || !screen) {
            return;
        }

        const screenObject = typeof screen === 'string' ? { screen } : screen;

        analytics.setCurrentScreen(screenObject.screen);
        analytics.logEvent({ ...screenObject, type: 'screen_view' });

        return () => {
            if (previousScreenRef.current) {
                analytics.setCurrentScreen(previousScreenRef.current);
            }
        };
    }, [isCurrentScreen, hasScreen, screenRef, ...deps]);
}
